<template>
  <div
    class="flex items-center gap-3 rounded-theme-md border border-theme-border bg-theme-surface px-3 py-2"
  >
    <!-- Status pill -->
    <span :class="['shrink-0 inline-flex items-center gap-1 text-[10px] uppercase tracking-wide font-medium px-1.5 py-0.5 rounded-full', statusPillClass]">
      {{ statusLabel }}
    </span>

    <!-- Tool + input summary -->
    <span class="flex-1 min-w-0">
      <span class="block truncate text-sm font-medium text-theme-text">
        {{ toolName }}
      </span>
      <span v-if="summaryInput" class="block truncate text-xs text-theme-text-muted">
        {{ summaryInput }}
      </span>
    </span>

    <!-- Started -->
    <span v-if="startedLabel" class="hidden shrink-0 tabular-nums text-[11px] text-theme-text-muted sm:inline">
      {{ startedLabel }}
    </span>

    <!-- Pin -->
    <AppButton
      type="button"
      variant="ghost"
      :tone="pinned ? 'primary' : 'muted'"
      size="xs"
      icon-only
      :icon="pinned ? mdiPin : mdiPinOutline"
      :aria-label="pinned ? t('ui.tool_run_unpin') : t('ui.tool_run_pin')"
      @click.stop="$emit('toggle-pin', !pinned)"
    />

    <!-- Retry — "Opnieuw uitvoeren" per briefing §6. -->
    <AppButton
      type="button"
      variant="ghost"
      tone="muted"
      size="xs"
      icon-only
      :icon="mdiRefresh"
      :aria-label="t('ui.tool_run_retry')"
      :title="t('ui.tool_run_retry')"
      @click.stop="$emit('retry')"
    />

    <!-- Open -->
    <AppButton
      type="button"
      variant="ghost"
      tone="primary"
      size="xs"
      :icon-right="mdiArrowRight"
      :aria-label="t('ui.tool_run_open')"
      @click.stop="$emit('open')"
    >
      {{ t('ui.tool_run_open') }}
    </AppButton>
  </div>
</template>

<script setup>
  /* eslint-disable no-undef */
  import AppButton from '@/components/ui/buttons/AppButton.vue';
  import {
    mdiArrowRight,
    mdiPin,
    mdiPinOutline,
    mdiRefresh,
  } from '@mdi/js';
  import { computed } from 'vue';
  import { useI18n } from 'vue-i18n';

  const props = defineProps({
    toolName: { type: String, required: true },
    summaryInput: { type: String, default: '' },
    /** 'ready' | 'running' | 'failed' */
    status: {
      type: String,
      default: 'ready',
      validator: (v) => ['ready', 'running', 'failed'].includes(v),
    },
    startedAt: { type: [String, Date, null], default: null },
    pinned: { type: Boolean, default: false },
  });

  defineEmits(['open', 'toggle-pin', 'retry']);
  const { t } = useI18n();

  const statusPillClass = computed(() => {
    if (props.status === 'running')
      return 'bg-theme-info/10 text-theme-info';
    if (props.status === 'failed')
      return 'bg-theme-danger/10 text-theme-danger';
    return 'bg-theme-success/10 text-theme-success';
  });

  const statusLabel = computed(() => {
    if (props.status === 'running') return t('ui.tool_run_status_running');
    if (props.status === 'failed') return t('ui.tool_run_status_failed');
    return t('ui.tool_run_status_ready');
  });

  const startedLabel = computed(() => {
    if (!props.startedAt) return '';
    const d = new Date(props.startedAt);
    if (Number.isNaN(d.getTime())) return '';
    const today = new Date();
    if (d.toDateString() === today.toDateString()) {
      return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    }
    return d.toLocaleDateString();
  });
</script>
